/**
 * Search & Browse Tab Component
 * 
 * Allows searching and browsing instruments with advanced filters.
 * Features:
 * - Symbol search with real-time filtering
 * - Exchange and instrument type filters
 * - Active/inactive status filtering
 * - Paginated results display
 * 
 * @module InstrumentMaster/tabs/SearchBrowseTab
 */

import React, { useState, useEffect } from 'react';
import { services } from '../../../../services/api';
import { useSnackbar } from '../../../../contexts/SnackbarContext';
import { FiSearch } from 'react-icons/fi';

const SearchBrowseTab = () => {
  const { showSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [instruments, setInstruments] = useState([]);
  const [filters, setFilters] = useState({
    symbol: '',
    exchange: '',
    instrument_type: '',
    status: 'active', // 'active', 'inactive', 'limited', 'all'
    page: 1,
    limit: 50,
  });
  const [totalResults, setTotalResults] = useState(0);

  const searchInstruments = async () => {
    setLoading(true);
    try {
      // Map status filter to API parameters
      const apiParams = {
        ...filters,
        is_active: undefined,
        is_tradable: undefined
      };

      if (filters.status === 'active') {
        apiParams.is_active = true;
      } else if (filters.status === 'inactive') {
        apiParams.is_active = false;
      } else if (filters.status === 'limited') {
        apiParams.is_active = true;
        apiParams.is_tradable = false;
      } else {
        // 'all' - explicitly request include_all=True to override active/tradable defaults
        apiParams.include_all = true;
        apiParams.is_active = null; 
      }
      
      // Remove internal status field
      delete apiParams.status;

      const response = await services.instruments.searchInstruments(apiParams);
      setInstruments(response.data.instruments || []);
      setTotalResults(response.data.total || 0);
    } catch (error) {
      showSnackbar(error.message || 'Failed to search instruments', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    searchInstruments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.page]);

  return (
    <div>
      {/* Filters */}
      <div className="bg-bg-3/50 p-4 rounded-lg mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Symbol
            </label>
            <input
              type="text"
              value={filters.symbol}
              onChange={(e) => setFilters({ ...filters, symbol: e.target.value, page: 1 })}
              placeholder="Search symbol..."
              className="w-full px-3 py-2 border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Exchange
            </label>
            <select
              value={filters.exchange}
              onChange={(e) => setFilters({ ...filters, exchange: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
            >
              <option value="">All Exchanges</option>
              <option value="NSE">NSE</option>
              <option value="BSE">BSE</option>
              <option value="NFO">NFO (Futures & Options)</option>
              <option value="MCX">MCX</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Type
            </label>
            <select
              value={filters.instrument_type}
              onChange={(e) => setFilters({ ...filters, instrument_type: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
            >
              <option value="">All Types</option>
              <option value="EQUITY">Equity</option>
              <option value="FUTURE">Future</option>
              <option value="OPTION">Option</option>
              <option value="COMMODITY">Commodity</option>
              <option value="CURRENCY">Currency</option>
              <option value="INDEX">Index</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-fg-2 mb-2">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ ...filters, status: e.target.value, page: 1 })}
              className="w-full px-3 py-2 border border-[var(--divider)] rounded-md bg-white dark:bg-bg-3 text-fg-1"
            >
              <option value="active">Active Only</option>
              <option value="limited">Limited Only (Non-Tradable)</option>
              <option value="inactive">Inactive Only</option>
              <option value="all">All</option>
            </select>
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <button
            type="button"
            onClick={searchInstruments}
            disabled={loading}
            className="btn btn-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <FiSearch />
            {loading ? 'Searching...' : 'Search'}
          </button>
        </div>
      </div>

      {/* Results */}
      <div>
        <div className="mb-4 flex justify-between items-center">
          <p className="text-sm text-fg-2">
            Found {totalResults} instrument{totalResults !== 1 ? 's' : ''}
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-bg-3">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Symbol
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Exchange
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  ISIN
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-fg-2 uppercase tracking-wider">
                  Last Synced
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-bg-1 divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-fg-2">
                    <div className="flex justify-center items-center gap-2">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                      Loading instruments...
                    </div>
                  </td>
                </tr>
              ) : instruments.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-4 text-center text-fg-2">
                    No instruments found. Try adjusting your filters.
                  </td>
                </tr>
              ) : (
                instruments.map((instrument) => (
                  <tr key={instrument.instrument_id} className="hover:bg-bg-3/50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-fg-1">
                      {instrument.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      {instrument.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      {instrument.exchange}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      <span className="ds-badge neutral sm">
                        {instrument.instrument_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2 font-mono">
                      {instrument.isin || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm flex gap-2">
                      {instrument.is_active ? (
                        <span className="ds-badge profit sm">
                          Active
                        </span>
                      ) : (
                        <span className="ds-badge neutral sm">
                          Inactive
                        </span>
                      )}

                      {!instrument.is_tradable && (
                        <span className="ds-badge warn sm" title="Limited trading data / Low liquidity">
                          Limited
                        </span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-fg-2">
                      {instrument.last_synced_at ? new Date(instrument.last_synced_at).toLocaleString() : 'Never'}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalResults > filters.limit && (
          <div className="mt-4 flex justify-between items-center">
            <button
              onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
              disabled={filters.page === 1}
              className="px-4 py-2 border border-[var(--divider)] rounded-md text-sm font-medium text-fg-2 hover:bg-bg-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-fg-2">
              Page {filters.page} of {Math.ceil(totalResults / filters.limit)}
            </span>
            <button
              onClick={() => setFilters({ ...filters, page: filters.page + 1 })}
              disabled={filters.page >= Math.ceil(totalResults / filters.limit)}
              className="px-4 py-2 border border-[var(--divider)] rounded-md text-sm font-medium text-fg-2 hover:bg-bg-3 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchBrowseTab;
